import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { AdjustmentAuthBody } from '@/api/client'
import { ADJUSTMENT_REASONS, type AdjustmentNeed, type AdjustmentReasonCode } from '@/lib/discountControls'
import { cn } from '@/lib/utils'

/**
 * One request waiting on a reason and/or a manager PIN (F-CTRL-N80 / F-FE-N213).
 * The counter opens it before sending a change its rules say needs them, and
 * again if the server refuses one (a stale terminal, a guessed total) — the
 * server is authoritative, this only asks.
 */
export interface PendingAdjustment {
  /** "Discount on the bill", "Service charge" … — the dialog title. */
  title: string
  /** One plain line saying what is being changed. */
  summary: string
  need: AdjustmentNeed
  /** Why the PIN is needed, in the till's words. */
  pinReason?: string
  /** Shown when the server refused the last attempt. */
  error?: string
  /** What was entered last time, kept when the dialog reopens after a refusal. */
  previous?: AdjustmentAuthBody
  run: (auth: AdjustmentAuthBody) => void
}

export function AdjustmentAuthDialog({ pending, onClose }: { pending: PendingAdjustment | null; onClose: () => void }) {
  const [code, setCode] = useState<AdjustmentReasonCode | null>(null)
  const [note, setNote] = useState('')
  const [pin, setPin] = useState('')

  useEffect(() => {
    if (!pending) return
    setCode((pending.previous?.reason_code as AdjustmentReasonCode | undefined) ?? null)
    setNote(pending.previous?.reason_note ?? '')
    setPin('')
  }, [pending])

  if (!pending) return null
  const { need } = pending
  const reasonOk = !need.reason || (code != null && (code !== 'other' || note.trim() !== ''))
  const pinOk = !need.pin || pin.length === 4
  const canApply = reasonOk && pinOk

  const apply = () => {
    if (!canApply) return
    pending.run({
      ...(code ? { reason_code: code } : {}),
      ...(code === 'other' ? { reason_note: note.trim() } : {}),
      ...(need.pin ? { pin } : {}),
    })
    onClose()
  }

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{pending.title}</DialogTitle>
          <DialogDescription>{pending.summary}</DialogDescription>
        </DialogHeader>

        {pending.error && (
          <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {pending.error}
          </p>
        )}

        {need.reason && (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-foreground">Why?</legend>
            <div className="flex flex-wrap gap-2">
              {ADJUSTMENT_REASONS.map(r => (
                <button
                  key={r.code}
                  type="button"
                  aria-pressed={code === r.code}
                  onClick={() => setCode(r.code)}
                  className={cn(
                    'h-11 rounded-full border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    code === r.code ? 'border-foreground bg-foreground text-background' : 'border-border text-foreground hover:bg-muted',
                  )}
                >
                  {r.label}
                </button>
              ))}
            </div>
            {code === 'other' && (
              <Input
                autoFocus
                maxLength={120}
                className="h-11"
                placeholder="A few words"
                aria-label="Reason"
                value={note}
                onChange={e => setNote(e.target.value)}
              />
            )}
          </fieldset>
        )}

        {need.pin && (
          <div className="space-y-1.5">
            <Label htmlFor="adjustment-pin" className="text-sm font-medium">
              Manager PIN
            </Label>
            <Input
              id="adjustment-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              autoFocus={!need.reason}
              className="h-11 w-32 text-base tracking-[0.3em]"
              placeholder="••••"
              value={pin}
              onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  apply()
                }
              }}
            />
            {pending.pinReason && <p className="text-xs text-muted-foreground">{pending.pinReason}</p>}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" className="h-11" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" className="h-11 px-6" disabled={!canApply} onClick={apply}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
